import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OptimizedImage from "./OptimizedImage";

import PrestationPortrait from "../img/Composant_presta/1.jpg";
import PrestationProjetsPros from "../img/Composant_presta/2.jpg";
import PrestationMariage from "../img/Composant_presta/3.jpg";

const DEFAULT_PRESTATIONS = [
  {
    id: "portraits",
    image: PrestationPortrait,
    imageAlt: "Portrait artistique en lumiere naturelle",
    category: "PORTRAIT",
    title: "Portrait Signature",
    summary: "Angles precis, lumiere maitrisee, emotions vraies.",
    detail:
      "Portrait Signature : 1h - 8 Photos retouchées et envoyées sous 48H - Moodboard et choix du lieu. Extérieur ou intérieur - 90€ TTC",
    tagline: "L'authenticite sublimee",
    link: "/services/portraits",
  },
  {
    id: "CORPORATE",
    image: PrestationProjetsPros,
    imageAlt: "Espace de travail lumineux avec materiel photo",
    category: "CORPORATE",
    title: "Image d'Entreprise",
    summary: "Une direction artistique coherente pour vos equipes.",
    detail:
      "Image d'entreprise : 8 Photos retouchées et livrées sous 48H - Moodboard et préparatifs aux derniers instants - Prix variable selon devis.",
    tagline: "Valorisez votre image",
    link: "/services/entreprise",
  },
  {
    id: "ÉVÉNEMENT",
    image: PrestationMariage,
    imageAlt: "Couple se tenant la main pendant un mariage",
    category: "EVENT",
    title: "ÉVÉNEMENT",
    summary: "Des recits vibrants pour vos moments forts.",
    detail:
      "Événement Signature : Remise des photos en ligne sous 48H - Nombre de photos variable selon événement (Jusqu’à 400) - Prix et durée shooting selon devis.",
    tagline: "Raconter votre histoire",
    link: "/services/evenementiel",
  },
  // Carte Boutique masquée temporairement (route /print)
];

const DEFAULT_HEADER = {
  eyebrow: null,
  title: "Services",
  description: "",
};

function Prestation({
  prestations,
  eyebrow = DEFAULT_HEADER.eyebrow,
  title = DEFAULT_HEADER.title,
  description = DEFAULT_HEADER.description,
  onCardSelect,
}) {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const observerRef = useRef(null);
  const [isSectionVisible, setIsSectionVisible] = useState(false);

  const cards = useMemo(() => {
    const source = prestations && prestations.length ? prestations : DEFAULT_PRESTATIONS;

    return source.map((item, index) => ({
      id: item.id || `prestation-${index}`,
      image: item.image || item.backgroundIMG || item.backgroundImg,
      imageAlt: item.imageAlt || "",
      category: item.category || item.eyebrow || item.tagline,
      title: item.title || item.titre,
      summary: item.summary || item.description,
      detail: item.detail || item.descriptionComplete,
      tagline: item.tagline || item.subtitle,
      link: item.link,
    }));
  }, [prestations]);

  useEffect(() => {
    const node = sectionRef.current;

    if (!node) {
      return undefined;
    }

    const shouldReduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (shouldReduceMotion) {
      setIsSectionVisible(true);
      return undefined;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsSectionVisible(true);
            if (observerRef.current) {
              observerRef.current.disconnect();
            }
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    observerRef.current.observe(node);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);


  // Effet "hover automatique" sur mobile
useEffect(() => {
  if (typeof window === "undefined" || window.innerWidth >= 768) return; // que mobile

  const cards = document.querySelectorAll(".prestations__card");
  const cardObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
        } else {
          entry.target.classList.remove("is-visible");
        }
      });
    },
    {
      threshold: 0.6, // quand 60% de la carte est visible
      rootMargin: "0px 0px -10% 0px",
    }
  );

  cards.forEach((card) => cardObserver.observe(card));

  return () => {
    cards.forEach((card) => cardObserver.unobserve(card));
  };
}, []);


  const handleCardClick = (item) => {
    if (typeof onCardSelect === "function") {
      onCardSelect(item);
      return;
    }

    if (item.link) {
      navigate(item.link);
    }
  };

  const handleCardKeyDown = (event, item) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick(item);
    }
  };

  const sectionClassName = [
    "prestations",
    "home-section",
    "home-section--flush-inline",
    isSectionVisible ? "prestations--visible" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      ref={sectionRef}
      className={sectionClassName}
      aria-label="Prestations photographiques"
    >
      <div className="prestations__inner">
        {(eyebrow || title || description) && (
          <header className="prestations__header">
            {eyebrow && <p className="prestations__eyebrow">{eyebrow}</p>}
            {title && <h2 className="prestations__title">{title}</h2>}
            {description && description.trim().length > 0 && (
              <p className="prestations__subtitle">{description}</p>
            )}
          </header>
        )}

        <div className="prestations__grid">
          {cards.map((item, index) => {
            const isInteractive = Boolean(onCardSelect || item.link);
            const ariaParts = [
              item.title,
              item.category,
              item.summary,
              item.detail,
              item.tagline,
            ].filter(Boolean);
            const cardClassName = [
              "prestations__card",
              isInteractive ? "prestations__card--interactive" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <article
                key={item.id}
                className={cardClassName}
                style={{ "--card-index": index }}
                role={isInteractive ? "link" : undefined}
                tabIndex={isInteractive ? 0 : undefined}
                onClick={isInteractive ? () => handleCardClick(item) : undefined}
                onKeyDown={isInteractive ? (event) => handleCardKeyDown(event, item) : undefined}
                aria-label={ariaParts.join(". ")}
              >
                <div className="prestations__card-media" aria-hidden="true">
                  {item.image && (
                    <OptimizedImage
                      src={item.image}
                      alt={item.imageAlt || `${item.title} - Service ${item.category} par Tanguy Perroit`}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                    />
                  )}
                </div>
                <span className="prestations__card-index">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="prestations__card-overlay" aria-hidden="true" />
                <div className="prestations__card-body">
                  {(item.category || item.tagline) && (
                    <span className="prestations__card-category">{item.category || item.tagline}</span>
                  )}
                  <h3 className="prestations__card-title">{item.title}</h3>
                  <div className="prestations__card-text">
                    <p className="prestations__card-text-content">{item.detail || item.summary}</p>
                  </div>
                </div>
                {isInteractive && (
                  <span className="prestations__card-cta" aria-hidden="true">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12h14"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13 7l6 5-6 5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Prestation;
